import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useBusiness } from './BusinessContext';
import { TalabMark } from './Logo';
interface WelcomeProps {
  /** Close and land on the setup guide. */
  onStart: () => void;
}

/**
 * Shown once, right after the hub is activated. Its only job is to explain the
 * shape of the journey in plain words and hand the merchant to step one — not
 * to tour every module, which is what left people lost before.
 */
export function BusinessHubWelcomeModal({
  onStart
}: WelcomeProps) {
  const {
    hub,
    markHubSeen
  } = useBusiness();
  const open = Boolean(hub?.active && hub?.isNew);
  const close = () => {
    markHubSeen();
    onStart();
  };
  const journey = [{
    title: 'Add what you sell',
    body: 'Talab learns your prices, stock and options.'
  }, {
    title: 'Customize and publish your shop',
    body: 'Your own page with an assistant that answers for you.'
  }, {
    title: 'Share your link',
    body: 'On WhatsApp, Instagram or your website.'
  }, {
    title: 'Follow up on leads and orders',
    body: 'Every conversation lands in your hub.'
  }];
  return <AnimatePresence data-id="element-608" data-mp="3k367ixiu0m2f">
      {open && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} role="dialog" aria-modal="true" aria-labelledby="hub-welcome-title" data-id="element-609" data-mp="3k47lb9r8fmnu">
          <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" data-id="element-610" data-mp="bte2qq3j1eq8" />

          <motion.div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden" initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-611" data-mp="zjgt91rsmcmq">
            <div className="px-8 pt-8" data-id="element-612" data-mp="zbsrxd69f8ft">
              <TalabMark size={44} data-id="element-613" data-mp="v834ws58gyww" />
              <h2 id="hub-welcome-title" className="font-display font-bold text-2xl text-dark-navy mt-5 leading-tight" data-id="element-614" data-mp="v95yj7txxfgi">
                Welcome{hub?.name ? `, ${hub.name}` : ''}
              </h2>
              <p className="text-[15px] text-slate-500 mt-2 leading-relaxed" data-id="element-615" data-mp="gj6yezitrtye">
                Your Business Hub is where you run your AI shop. Here is the
                whole journey — we’ll take you through it one step at a time.
              </p>
            </div>

            {/* The order is the point, so it is shown as a sequence */}
            <ol className="px-8 mt-6 relative" data-id="element-616" data-mp="2j52lw242i40c">
              {journey.map((step, i) => <li key={step.title} className="relative flex gap-4 pb-5 last:pb-0" data-id="element-617" data-mp="2n5w88r1v3ar4" data-mpi={i}>
                  {i < journey.length - 1 && <span className="absolute left-[13px] top-7 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-618" data-mp="2s4hcr2o5cgld" />}
                  <span className="relative w-7 h-7 rounded-full bg-primary-indigo/[0.08] text-primary-indigo text-[12px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-619" data-mp="1zfjx77bbkxwb">
                    {i + 1}
                  </span>
                  <div className="pt-0.5" data-id="element-620" data-mp="2e715xq1xukt3">
                    <p className="text-sm font-semibold text-dark-navy" data-id="element-621" data-mp="2e83wtxz9gn90">
                      {step.title}
                    </p>
                    <p className="text-[13px] text-slate-500 mt-0.5" data-id="element-622" data-mp="1lq8b84gzfq3h">
                      {step.body}
                    </p>
                  </div>
                </li>)}
            </ol>

            <div className="px-8 py-7 mt-2 flex items-center justify-between gap-4" data-id="element-623" data-mp="3jdso2u43vlm5">
              <p className="text-[12px] text-slate-400" data-id="element-624" data-mp="3jbzeb5y74l9x">About 10 minutes</p>
              <button onClick={close} autoFocus className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary-indigo text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-625" data-mp="1147hrxus1op">
                Start with step 1
                <ArrowRightIcon className="w-4 h-4" data-id="element-626" data-mp="618nzhyrgwex" />
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}